import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import React, { useState } from "react";
import {
  useDataProvider,
  useMutation,
  useNotify,
  useRefresh,
} from "react-admin";
import { Booking } from "../../types";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
    padding: "1rem",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
  },
});

const BookingIssue: React.FC<{ record?: Booking }> = ({ record }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const [bookingIssueId, setBookingIssueId] = useState<number>();
  const [actionType, setActionType] = useState("");

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState("");

  const [handleAction, { loading }] = useMutation(
    {
      type: "update",
      resource: `bookings`,
      payload: {
        id: record?.id ?? "",
        data: {
          action: "update_issue",
          booking_issue_id: bookingIssueId ?? "",
          issue_status: actionType,
        },
      },
    },
    {
      onSuccess: () => {
        notify(`Booking issue has been ${actionType}.`, "info");
        setOpen(false);
        refresh();
      },
      onFailure: (error) =>
        notify(`Error when updating the booking: ${error.message}`, "warning"),
    },
  );

  const executeBulkAction = async () => {
    if (!record || selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      // Execute sequentially to avoid concurrent update issues on the same Booking model
      for (const id of selectedIds) {
        await dataProvider.update("bookings", {
          id: record.id,
          data: {
            action: "update_issue",
            booking_issue_id: id,
            issue_status: bulkActionType,
          },
          previousData: record,
        });
      }
      notify(`Selected booking issues have been ${bulkActionType}.`, "info");
      setSelectedIds([]);
      setBulkDialogOpen(false);
      refresh();
    } catch (error: any) {
      notify(`Error when updating bookings: ${error.message}`, "warning");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleClickOpen = (type: string, id: number) => {
    setActionType(type);
    setBookingIssueId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleBulkClose = () => {
    setBulkDialogOpen(false);
  };

  if (!record) return <>Loading...</>;
  const { booking_issues } = record;
  if (booking_issues) {
    booking_issues.sort(
      (a, b) =>
        a.title.localeCompare(b.title) ||
        a.description.localeCompare(b.description),
    );
  }

  const validIssues =
    booking_issues?.filter((item) => item.status !== "cancelled") || [];
  const openIssues = validIssues.filter((item) => item.status === "open");

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = openIssues.map((n) => n.id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const handleCheckboxClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number,
  ) => {
    event.stopPropagation();
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }

    setSelectedIds(newSelected);
  };

  // const getAllocations = (item: BookingItem, booking: Booking) => {
  //   return booking.allocations.filter(
  //     (allocation) => allocation.product_id === item.product_id
  //   );
  // };

  // const getDeliveries = (item: BookingItem, booking: Booking) => {
  //   return booking.deliveries.filter(
  //     (delivery) => delivery.product_instance.product_id === item.product_id
  //   );
  // };

  const isSelected = (id: number) => selectedIds.indexOf(id) !== -1;

  return (
    <>
      <TableContainer style={{ margin: "2rem auto" }}>
        {selectedIds.length > 0 && (
          <Box className={classes.toolbar}>
            <Typography variant="subtitle1">
              {selectedIds.length} selected
            </Typography>
            <Box>
              <Button
                style={{ marginRight: "0.25rem" }}
                variant="contained"
                size="small"
                color="primary"
                onClick={() => {
                  setBulkActionType("accepted");
                  setBulkDialogOpen(true);
                }}
              >
                Accept Selected
              </Button>
              <Button
                variant="contained"
                size="small"
                color="secondary"
                onClick={() => {
                  setBulkActionType("rejected");
                  setBulkDialogOpen(true);
                }}
              >
                Reject Selected
              </Button>
            </Box>
          </Box>
        )}
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < openIssues.length
                  }
                  checked={
                    openIssues.length > 0 &&
                    selectedIds.length === openIssues.length
                  }
                  onChange={handleSelectAllClick}
                  disabled={openIssues.length === 0}
                />
              </TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validIssues.map((item) => {
              const isItemSelected = isSelected(item.id);
              const isOpen = item.status === "open";

              return (
                <TableRow key={item.id} hover selected={isItemSelected}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isItemSelected}
                      onChange={(event) => handleCheckboxClick(event, item.id)}
                      disabled={!isOpen}
                    />
                  </TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.status}</TableCell>
                  <TableCell>
                    {isOpen ? (
                      <>
                        <Button
                          style={{ marginRight: "0.25rem" }}
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => handleClickOpen("accepted", item.id)}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          color="secondary"
                          onClick={() => handleClickOpen("rejected", item.id)}
                        >
                          Reject
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        disabled
                        color="inherit"
                      >
                        {item.status === "accepted" ? "Accepted" : "Rejected"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {actionType === "accepted"
            ? "Accept Booking Issue?"
            : "Reject Booking Issue?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to{" "}
            {actionType === "accepted" ? "accept" : "reject"} this booking
            issue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Back
          </Button>
          <Button
            onClick={handleAction}
            color="primary"
            disabled={loading}
            autoFocus
          >
            {loading ? "Please wait..." : `Confirm ${actionType}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDialogOpen}
        aria-labelledby="bulk-alert-dialog-title"
        aria-describedby="bulk-alert-dialog-description"
      >
        <DialogTitle id="bulk-alert-dialog-title">
          {bulkActionType === "accepted"
            ? "Accept Selected Booking Issues?"
            : "Reject Selected Booking Issues?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-alert-dialog-description">
            Are you sure you want to{" "}
            {bulkActionType === "accepted" ? "accept" : "reject"} the{" "}
            {selectedIds.length} selected booking issues?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkClose} color="secondary">
            Back
          </Button>
          <Button
            onClick={executeBulkAction}
            color="primary"
            disabled={bulkLoading}
            autoFocus
          >
            {bulkLoading ? "Please wait..." : `Confirm ${bulkActionType}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default BookingIssue;
